import { Color } from '../../../../chess/types'
import { GameResult, GameResultType, PlayerInGameData } from '../../../../store/types'
import { GAME_END_REASONS } from '../gameServerConnector/GSTypes'

export interface DialogDataEntry {
    winnerMessage?: string
    description: string
    otherMessage: string
    neutralMessage: string
    neutralDescription?: string
    header: string
}

export const dialogData: Record<string, DialogDataEntry> = {
    [GameResultType.Checkmated + Color.White]: {
        winnerMessage: 'You won!',
        description: 'Black was checkmated',
        otherMessage: 'You lost!',
        neutralMessage: 'White won!',
        header: 'white-won.svg',
    },
    [GameResultType.Checkmated + Color.Black]: {
        winnerMessage: 'You won!',
        description: 'White was checkmated',
        otherMessage: 'You lost!',
        neutralMessage: 'Black won!',
        header: 'black-won.svg',
    },
    [GameResultType.ForfeitedOnTime + Color.White]: {
        winnerMessage: 'You won!',
        description: 'Black forfeited on time',
        otherMessage: 'You lost!',
        neutralMessage: 'White won!',
        header: 'white-won.svg',
    },
    [GameResultType.ForfeitedOnTime + Color.Black]: {
        winnerMessage: 'You won!',
        description: 'White forfeited on time',
        otherMessage: 'You lost!',
        neutralMessage: 'Black won!',
        header: 'black-won.svg',
    },
    [GameResultType.Resigned + Color.White]: {
        winnerMessage: 'You won!',
        description: 'Black resigned',
        otherMessage: 'You lost!',
        neutralMessage: 'White won!',
        header: 'white-won.svg',
    },
    [GameResultType.Resigned + Color.Black]: {
        winnerMessage: 'You won!',
        description: 'White resigned',
        otherMessage: 'You lost!',
        neutralMessage: 'Black won!',
        header: 'black-won.svg',
    },
    // [GameResultType.NoPlayerMoveSinceStart + Color.White]: {
    //     winnerMessage: 'Game aborted',
    //     description: 'Black did not move',
    //     otherMessage: 'Game aborted',
    // },
    // [GameResultType.NoPlayerMoveSinceStart + Color.Black]: {
    //     winnerMessage: 'Game aborted',
    //     description: 'White did not move',
    //     otherMessage: 'Game aborted',
    // },
    [GameResultType.PlayerDisconnected + Color.White]: {
        winnerMessage: 'You won!',
        description: 'Black disconnected and forfeits',
        otherMessage: 'You lost!',
        neutralMessage: 'White won!',
        header: 'white-won.svg',
    },
    [GameResultType.PlayerDisconnected + Color.Black]: {
        winnerMessage: 'You won!',
        description: 'White disconnected and forfeits',
        otherMessage: 'You lost!',
        neutralMessage: 'Black won!',
        header: 'black-won.svg',
    },
    [GameResultType.AllPlayersDisconnected]: {
        otherMessage: 'Game aborted',
        description: 'Both players disconnected',
        neutralMessage: 'Game aborted',
        header: 'game-aborted.svg',
    },
    [GameResultType.NoPlayerMoveSinceStart]: {
        otherMessage: 'Game aborted',
        description: 'Opening move not made',
        neutralMessage: 'Game aborted',
        header: 'game-aborted.svg',
    },
    [GameResultType.Draw]: {
        otherMessage: 'Game drawn',
        description: 'Draw agreed',
        neutralMessage: 'Game drawn',
        neutralDescription: 'Players agreed on a draw',
        header: 'game-drawn.svg',
    },
    [GameResultType.InsufficientMaterial]: {
        otherMessage: 'Game drawn',
        description: 'Insufficient material',
        neutralMessage: 'Game drawn',
        header: 'game-drawn.svg',
    },
    [GameResultType.Stalemate]: {
        otherMessage: 'Game drawn',
        description: 'Stalemate',
        neutralMessage: 'Game drawn',
        header: 'game-drawn.svg',
    },
    [GameResultType.ThreefoldRepetition]: {
        otherMessage: 'Game drawn',
        description: 'Drawn by threefold repetition',
        neutralMessage: 'Game drawn',
        header: 'game-drawn.svg',
    },
    [GameResultType.FivefoldRepetition]: {
        otherMessage: 'Game drawn',
        description: 'Drawn by fivefold repetition',
        neutralMessage: 'Game drawn',
        header: 'game-drawn.svg',
    },
    [GameResultType.FiftyMoveRule]: {
        otherMessage: 'Game drawn',
        description: 'Drawn by fifty move rule',
        neutralMessage: 'Game drawn',
        header: 'game-drawn.svg',
    },
    [GameResultType.SeventyFiveMoveRule]: {
        otherMessage: 'Game drawn',
        description: 'Drawn by seventy five move rule',
        neutralMessage: 'Game drawn',
        header: 'game-drawn.svg',
    },
    [GameResultType.Aborted]: {
        otherMessage: 'Game aborted',
        description: 'Rating is not affected',
        neutralMessage: 'Game aborted',
        header: 'game-aborted.svg',
    },
    [GameResultType.TechnicalErrorAi]: {
        otherMessage: 'Game aborted',
        description: 'Technical error',
        neutralMessage: 'Game aborted',
        header: 'game-aborted.svg',
    },
    [GameResultType.Unknown]: {
        winnerMessage: 'Game ended',
        otherMessage: 'Game ended',
        description: 'Unknown reason',
        neutralMessage: 'Game ended',
        header: 'game-ended.svg',
    },
    [GameResultType.Timeout]: {
        otherMessage: 'Game Timeout',
        description: 'Timeout',
        neutralMessage: 'Game Timeout',
        header: 'game-timeout.svg',
    },
    [GameResultType.SecondPlayerTimeout]: {
        otherMessage: 'Game Aborted',
        description: 'opponent failed to connect',
        neutralMessage: 'Game aborted',
        header: 'game-aborted.svg',
    },
}

export const isAWin = (winnerId: string | undefined, player: PlayerInGameData) => {
    return winnerId && winnerId === (player.playerKind === 'machine' ? 'machine' : player.playerId)
}

export const isADraw = (result: { winnerId: string | undefined, type: GameResultType }) => {
    const drawTypes = [
        GameResultType.Draw,
        GameResultType.FiftyMoveRule,
        GameResultType.FivefoldRepetition,
        GameResultType.InsufficientMaterial,
        GameResultType.SeventyFiveMoveRule,
        GameResultType.Stalemate,
        GameResultType.ThreefoldRepetition,
    ]

    return result.winnerId === undefined && drawTypes.includes(result.type)
}

export const getGameResultType = (reason: GAME_END_REASONS) => {
    switch (reason) {
        case GAME_END_REASONS.CHECK_MATE:
            return GameResultType.Checkmated
        case GAME_END_REASONS.RESIGNATION:
            return GameResultType.Resigned
        case GAME_END_REASONS.INSUFFICIENT_MATERIAL:
            return GameResultType.InsufficientMaterial
        case GAME_END_REASONS.STALE_MATE:
            return GameResultType.Stalemate
        case GAME_END_REASONS.THREE_FOLD_REPETITION:
            return GameResultType.ThreefoldRepetition
        case GAME_END_REASONS.FIVE_FOLD_REPETITION:
            return GameResultType.FivefoldRepetition
        case GAME_END_REASONS.FIFTY_MOVE_RULE:
            return GameResultType.FiftyMoveRule
        case GAME_END_REASONS.SEVENTY_FIVE_MOVE_RULE:
            return GameResultType.SeventyFiveMoveRule
        case GAME_END_REASONS.DRAW:
            return GameResultType.Draw
        case GAME_END_REASONS.ABORTED:
            return GameResultType.Aborted
        case GAME_END_REASONS.SECOND_PLAYER_JOIN_TIMEOUT:
            return GameResultType.SecondPlayerTimeout
        case GAME_END_REASONS.CLOCK_EXPIRATION:
            return GameResultType.ForfeitedOnTime
        case GAME_END_REASONS.TECHNICAL_ERROR_AI:
            return GameResultType.TechnicalErrorAi
        case GAME_END_REASONS.NO_PLAYER1_MOVE_SINCE_START:
        case GAME_END_REASONS.NO_PLAYER2_MOVE_SINCE_START:
            return GameResultType.NoPlayerMoveSinceStart
        case GAME_END_REASONS.ALL_PLAYERS_DISCONNECTED:
            return GameResultType.AllPlayersDisconnected
        case GAME_END_REASONS.PLAYER1_DISCONNECTED:
        case GAME_END_REASONS.PLAYER2_DISCONNECTED:
            return GameResultType.PlayerDisconnected
        case GAME_END_REASONS.REASON_NORMAL:
        default:
            return GameResultType.Unknown
    }
}

const winFeedbacks = [
    "Your discipline is showing. This is how wining habits are built.",
    "Great game! Chess is time well spent for your mind.",
    "Strong win; your discipline is clearly growing.",
    "You earned this result through focused play.",
    "Great match, build on this momentum.",
    "You are becoming real threat on the board! Love seeing it!",
    "Fortune favors the bold.",
    "Strong foundations make strong finishes.",
    "The more you practice, the luckier you get.",
    "Act as if what you do makes a difference. It does.",
    "Success is the sum of small efforts, repeated day in and day out.",
    "Small improvements add up.",
    "Steady effort pays.",
    "Build the habit; results follow.",
    "Precision follows understanding.",
    "Consistency is quiet, but it wins.",
    "Consistency beats intensity.",
    "Trust the work.",
    "Make one good decision, then another.",
    "Clarity comes from repetition.",
    "What you repeat, you become.",
    "Keep going. Keep learning.",
    "Focus on the process.",
    "Well begun is half done.",
    "The secret of getting ahead is getting started.",
    "An investment in knowledge pays the best interest.",
    "Without continual growth and progress, such words as improvement have no meaning.",
    "Start where you are. Use what you have. Do what you can.",
    "Do what you can, with what you have, where you are.",
    "Even short sessions can train the skill of returning attention after distraction.",
    "Chess can be a social wellness tool: shared focus, shared challenge.",
    "Gukesh Dommaraju became the youngest world chess champion at 18 (Dec 12, 2024).",
    "The highest FIDE rating ever recorded is 2882, achieved by Magnus Carlsen in May 2014.",
    "Magnus Carlsen became world No. 1 at age 19, one of the youngest to reach the top ranking.",
    "The UN designated July 20 as World Chess Day.",
    "Garry Kasparov was the youngest world champion at 22 until Gukesh broke the record in 2024.",
    'The 1972 Fischer-Spassky match in Reykjavik is often called "the Match of the Century.',
    'Benjamin Franklin wrote an essay called "The Morals of Chess", December 1786.',
    "Benjamin Franklin linked chess to habits like foresight, caution, and perseverance.",
    "The first Women's World Chess Champion was Vera Menchik (1927).",
    "FIDE adopted the Elo rating system in 1970.",
    "Chess960 (Fischer Random) was introduced by Bobby Fischer in 1996.",
    "The fastest possible checkmate is Fool's Mate, which can happen in two moves.",
    "Castling took its modern form in the 17th century.",
]

const lossFeedbacks = [
    "Nice fight, these are the games that make you stronger.",
    "Every game, win or lose, grows your skills.",
    "This game boosted your focus and concentration.",
    "Win or lose, playing keeps your thinking sharp.",
    "Chess moments like this strengthen calm thinking under pressure.",
    "Every game adds to your intuition, no matter the result.",
    "Every game helps you learn new smart ideas.",
    "Fall seven times, stand up eight.",
    "It does not matter how slowly you go as long as you do not stop.",
    "Our greatest glory is not in never falling, but in rising every time we fall.",
    "Difficulties strengthen the mind, as labor does the body. The obstacle is the way.",
    "If it is endurable, then endure it.",
    "Perseverance conquers all.",
    "Smooth seas do not make skillful sailors.",
    "A stumble may prevent a fall.",
    "Perseverance is not a long race; it is many short races one after another.",
    "Success is not final, failure is not fatal: it is the courage to continue that counts.",
    "It always seems impossible until it's done.",
    "The best time to plant a tree was 20 years ago. The second best time is now.",
    "Small steps still move you forward.",
    "Pause. Refocus. Continue.",
    "Stay patient with your progress.",
    "Stronger habits beat stronger moods.",
    "Try again with one small change.",
    "Turn the result into a lesson.",
    "The lesson matters more than the score.",
    "Hard things get easier with repetition.",
    "You don't have to be perfect to improve.",
    "If you're tired, slow down. Don't stop.",
    "Chess turns frustration into a routine: pause, reassess, continue.",
    "Chess helps you rehearse resilience: lose a piece, find a plan.",
    "Chess rewards calm breathing and slower thinking under pressure.",
    "Chess is practice for patience: good decisions beat fast decisions.",
    "Some research suggests chess training can support attention and executive skills in learners.",
    "Some studies suggest structured chess instruction may help academic and cognitive outcomes more than casual play.",
    "Some research in older adults explores chess as a mentally stimulating activity linked to cognitive health and quality of life.",
    "Chess is often discussed as supporting cognitive reserve: keeping the mind active over time.",
    "Chess is a safe place to practice handling pressure.",
    "Chess promotes delayed gratification: invest now, benefit later.",
    "Sergey Karjakin became a grandmaster at 12 years and 7 months, one of the youngest ever.",
    "Judit Polgár became a grandmaster at 15 years and 4 months, breaking Fischer's record at the time.",
    "Bobby Fischer had a famous 20 game winning streak across elite events in 1970 to 1971.",
    "The longest master game on record lasted 269 moves (Nikolić vs Arsović, 1989).",
    "Stalemate is a draw: no legal move, and the king is not in check.",
    "The first official World Chess Championship match was Steinitz vs Zukertort (1886).",
    "FIDE (the world chess federation) was founded in Paris in 1924.",
    "The first known telegraph chess match was Washington vs Baltimore (1844).",
    "The first Chess Olympiad was held in London in July 1927.",
    "The Women's Chess Olympiad has been held by FIDE since 1957.",
    "PGN is a plain-text standard for recording chess games.",
    "The earliest surviving printed chess book was published around 1497 in Salamanca, Spain.",
    "The Lewis chessmen are famous medieval pieces discovered in 1831.",
    'The Mechanical Turk was a famous chess-playing "machine" first shown in 1770.',
    "The Mechanical Turk played famous figures including Napoleon.",
    "IBM's Deep Blue 1997 win was the first defeat of a reigning world champion by a computer in match play.",
    "The Queen's Gambit series is based on a 1983 novel by Walter Tevis.",
    "The Queen's Gambit was watched by over 62 million households in its first 28 days on Netflix.",
    "Stanley Kubrick used to hustle chess in New York before his acting career took off.",
    "In 2001: A Space Odyssey (1968), astronaut Frank Poole plays chess against HAL 9000.",
    "Hard game. Still good training for focus and composure.",
]

const drawFeedbacks = [
    "Solid draw, your consistency is improving.",
    "You held firm, stay on this trajectory.",
    "Good discipline, progress is taking shape.",
    "You matched pressure well, keep developing.",
    "A steady result that builds long-term strength.",
    "A fair result building strategic stability.",
    "Holding steady strengthens long-term focus.",
    "Resilience carried you through key positions.",
    "You maintained balance in a demanding duel.",
    "This game boosted your focus and concentration.",
    "A stitch in time saves nine.",
    "Patience is bitter, but its fruit is sweet.",
    "Calm builds consistency.",
    "Keep it simple. Keep it steady.",
    "Build, don't rush.",
    "Better decisions come from slower thinking.",
    "A watched pot never boils.",
    "Measure twice, cut once.",
    "Patience turns pressure into clarity.",
    "If you can't do it fast, do it right.",
    "Don't chase speed; chase accuracy.",
    "A steady pace beats a rushed sprint.",
    "When in doubt, simplify.",
    "Calm first, then clever.",
    "You don't need perfect, you just need practice.",
    "Be not afraid of growing slowly; be afraid only of standing still.",
    "The man who moves a mountain begins by carrying away small stones.",
    "Little by little, one travels far.",
    "You have power over your mind, not outside events.",
    "Make the best use of what is in your power.",
    "No great thing is created suddenly.",
    "First say to yourself what you would be; then do what you have to do.",
    "A journey of a thousand miles begins with a single step.",
    "Practice makes patterns familiar.",
    "Every attempt teaches something.",
    "Errors are information.",
    "Mistakes are information.",
    "Errors are feedback.",
    "Confidence is built by repetition.",
    "Accuracy grows with exposure.",
    "Short-term results vary. Habits compound.",
    "Results fluctuate. Effort compounds.",
    "Make the lesson the reward.",
    "The idea matters more than the move order.",
    "Learn the pattern, not the result.",
    "Make the next attempt a little better.",
    "Measure progress in lessons learned.",
    "Progress is built in the boring minutes.",
    "Even the best plan needs a second look.",
    "Small corrections beat big regrets.",
    "Keep your head; your hands will follow.",
    "Discipline creates consistency.",
    "Consistency creates confidence.",
    "Review, then reset.",
    "One puzzle at a time.",
    "The Staunton piece design debuted in 1849 and became the global standard for competitive chess sets.",
    'In 1999, Kasparov played "Kasparov vs the World" online against a team of over 50,000 people voting on moves.',
    `Claude Shannon's 1950 paper "Programming a Computer for Playing Chess" helped launch computer chess research.`,
    'The "Shannon number" estimates the game tree complexity of chess at roughly 10^120 possible games.',
    "Chess is a full attention workout: focus, plan, execute, repeat.",
    "Chess trains working memory: hold ideas, compare options, choose one.",
    "Chess gives your brain reps: pattern recognition, planning, evaluation.",
    "A chess session is mental cardio: sustained concentration over time.",
]

const getRandomFeedback = (messages: Array<string>) => {
    return messages[Math.floor(Math.random() * messages.length)]
}

export const getFeedbackMessage = (result: GameResult, myColor?: Color) => {
    if (myColor && result.winner) {
        return myColor === result.winner
            ? getRandomFeedback(winFeedbacks)
            : getRandomFeedback(lossFeedbacks)
    } else if (isADraw({ winnerId: undefined, type: result.type })) {
        return getRandomFeedback(drawFeedbacks)
    }
    return ''
}

export const getGameOverData = (resultType: GameResultType, winner?: Color): DialogDataEntry => {
    const dataKey = resultType + (winner ? winner : '')
    return !!dialogData[dataKey] ? dialogData[dataKey] : dialogData.unknown
}